import { errorIsStatus, useClientPortalAPI } from "@certn/api";
import { Stack, Text } from "@certn/cascadia";
import {
  ColumnDefinition,
  Container,
  DataTable,
  MediaQuery,
  Pagination,
  SectionMessage,
} from "@certn/cascadia-react";
import {
  orderingParamToSortDescriptor,
  sortDescriptorToOrderingParam,
} from "@certn/cascadia-react/components/data-tables/utils";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { FilterBar } from "../../components/filters/FilterBar";
import { PageTitle } from "../../components/page-title/PageTitle";
import { useQueryParams } from "../../hooks/useQueryParams";
import { Schemas } from "../../types";
import { getCommonTableColumns } from "../templates/utils";
import { queryParamsToApiParams } from "../utils";

export const FitnessProprietyTemplateListView = () => {
  const navigate = useNavigate();
  const { params, setParam } = useQueryParams();
  const { t } = useTranslation();

  const { data, isLoading, error } = useClientPortalAPI(
    "/api/internal/client-portal/fitness-propriety-templates/",
    {
      ...queryParamsToApiParams(params),
    }
  );

  const columns: ColumnDefinition<Schemas["FitnessProprietyTemplatesList"]>[] =
    getCommonTableColumns<Schemas["FitnessProprietyTemplatesList"]>({
      actions: () => null,
      translate: t,
      hideActions: true,
    });

  return (
    <Container layout="full-width">
      <PageTitle>{t("Fitness & propriety")}</PageTitle>
      <MediaQuery sm type="hide">
        <Stack orientation="horizontal" gap="space-2">
          <Text color="text-weak">
            {t(
              "Standard fitness and propriety declaration template for your organization. Content is managed by Certn and cannot be edited."
            )}
          </Text>
        </Stack>
      </MediaQuery>
      {error && !errorIsStatus(error, 404) && (
        <SectionMessage
          type="danger"
          icon="error"
          sectionTitle={t("Unable to retrieve fitness & propriety templates")}
        >
          {String(error.message)}
        </SectionMessage>
      )}
      <FilterBar testID="fitness-propriety-templates" hasSearch />
      <DataTable
        isLoading={isLoading}
        onSortChange={(sortDescriptor) =>
          setParam("ordering", sortDescriptorToOrderingParam(sortDescriptor))
        }
        sortDescriptor={orderingParamToSortDescriptor(params.ordering)}
        columns={columns}
        rows={data?.results ? data.results : []}
        aria-label={t("Fitness & Propriety Templates Table")}
        testId="fitness-propriety-template-list-view-data-table"
        onRowAction={(id) => {
          navigate(`${id}`);
        }}
      />
      <Pagination
        setPage={(page) => setParam("page", page.toString())}
        setPageCount={(pageCount) =>
          setParam("page_size", pageCount.toString())
        }
        total={data?.count ?? 0}
        page={params.page ? Number(params.page) : 1}
        pageCount={params["page_size"] ? Number(params["page_size"]) : 25}
      />
    </Container>
  );
};
